import axios from 'axios';

/** i18n key for unexpected failures; never expose raw stack traces or server internals. */
export const DEFAULT_USER_ERROR_KEY = 'apiError.default';

const API_ERROR_CODE_TO_KEY: Record<string, string> = {
  server_error: 'apiError.server_error',
  not_found: 'apiError.not_found',
  invalid_json: 'apiError.invalid_json',
  invalid_csrf_token: 'apiError.invalid_csrf_token',
  draft_access_required: 'apiError.draft_access_required',
  invalid_or_expired_draft_token: 'apiError.invalid_or_expired_draft_token',
  token_does_not_match_application: 'apiError.token_does_not_match_application',
  token_does_not_match_client: 'apiError.token_does_not_match_client',
  too_many_requests: 'apiError.too_many_requests',
  client_guid_required: 'apiError.client_guid_required',
  invalid_client_guid: 'apiError.invalid_client_guid',
  client_not_found: 'apiError.client_not_found',
  client_disabled: 'apiError.client_disabled',
  service_line_required: 'apiError.client_not_found',
  validation_failed: 'apiError.validation_failed',
  missing_body_signature: 'apiError.missing_body_signature',
  invalid_body_signature: 'apiError.invalid_body_signature',
  body_signature_unavailable: 'apiError.body_signature_unavailable',
};

function normalizeCode(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const t = raw.trim();
  if (API_ERROR_CODE_TO_KEY[t]) return t;
  const compact = t.replace(/\s+/g, '_').toLowerCase();
  if (API_ERROR_CODE_TO_KEY[compact]) return compact;
  return undefined;
}

/**
 * Maps API error codes and HTTP status to an i18n key (use with `t()` in the UI).
 */
export function getUserFacingApiError(err: unknown): string {
  if (!axios.isAxiosError(err) || !err.response) {
    if (axios.isAxiosError(err) && (err.code === 'ECONNABORTED' || err.message === 'Network Error')) {
      return 'apiError.network';
    }
    return DEFAULT_USER_ERROR_KEY;
  }

  const status = err.response.status;
  const raw = (err.response.data as { error?: string } | undefined)?.error;
  const code = normalizeCode(typeof raw === 'string' ? raw : undefined);

  if (code) {
    return API_ERROR_CODE_TO_KEY[code] ?? DEFAULT_USER_ERROR_KEY;
  }

  if (status >= 500) {
    return DEFAULT_USER_ERROR_KEY;
  }
  if (status === 429) {
    return API_ERROR_CODE_TO_KEY.too_many_requests;
  }
  if (status === 404) {
    return API_ERROR_CODE_TO_KEY.not_found;
  }
  if (status === 401) {
    return API_ERROR_CODE_TO_KEY.invalid_or_expired_draft_token;
  }
  if (status === 403) {
    if (raw === 'invalid_csrf_token') {
      return API_ERROR_CODE_TO_KEY.invalid_csrf_token;
    }
    if (raw === 'client_disabled') {
      return API_ERROR_CODE_TO_KEY.client_disabled;
    }
    if (raw === 'client_mismatch') {
      return API_ERROR_CODE_TO_KEY.client_not_found;
    }
    return DEFAULT_USER_ERROR_KEY;
  }
  return DEFAULT_USER_ERROR_KEY;
}
